import React from "react";
import { View, StyleSheet, Image, Pressable, Platform } from "react-native";
import { Text } from "../text/text";
import { useTheme } from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { startCase, upperCase } from "lodash";
import { toWebUIDollars } from "../../utils/money";
import moment from "moment";
import { translate } from "../../i18n";
import { BottomSheetModal } from "../new/bottom-sheet-modal/bottom-sheet-modal";
const constants = require("domuso-constants");
const { PaymentTypes, PaymentSourceTypes } = constants.payment;
const { CheckType } = constants.check21;

interface HistoryBottomSheetProps {
  sheetRef: any;
  data: any;
  showLeftNavButton: boolean;
  showRightNavButton: boolean;
  leftClickHandler: Function;
  rightClickHandler: Function;
}

export const HistoryBottomSheet = ({
  sheetRef,
  data,
  showLeftNavButton,
  showRightNavButton,
  leftClickHandler,
  rightClickHandler,
}: HistoryBottomSheetProps) => {
  const theme = useTheme() as ThemeType;
  const { colors, spacing, fontSize, iconSize } = theme;
  const styles = StyleSheet.create({
    header: {
      borderBottomWidth: 1,
      borderBottomColor: colors.lightGrey,
      width: "98%",
      marginHorizontal: "1%",
    },
    headerRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: spacing.tiny,
      marginBottom: spacing.smaller,
    },
    alignRight: {
      justifyContent: "flex-end",
    },
    navButtonContainer: {
      width: 30,
    },
    buttonContainer: {
      padding: spacing.small,
      borderRadius: spacing.smaller,
      backgroundColor: colors.lightestGrey,
      width: iconSize.medium,
      height: iconSize.medium,
      alignItems: "center",
      justifyContent: "center",
    },
    navButton: {
      width: iconSize.smaller,
      height: iconSize.smaller,
    },
    title: { fontSize: fontSize.largerPlus, color: colors.darkBg },
    container: {
      width: "90%",
      marginHorizontal: "5%",
      marginTop: spacing.medium,
    },
    row: {
      flexDirection: "row",
      width: "100%",
      height: 40,
      marginVertical: spacing.small,
      justifyContent: "space-between",
    },
    column: { flexDirection: "row", flex: 1 },
    lineContainer: {
      flex: 0.01,
      marginRight: spacing.smaller,
      flexDirection: "column",
      justifyContent: "center",
      height: "100%",
    },
    line: {
      backgroundColor: colors.primary,
      width: 2,
      height: "80%",
    },
    textContainer: { flex: 1, flexDirection: "column", justifyContent: "space-between" },
    bold: { fontWeight: "bold" },
    smallText: { fontSize: fontSize.small },
    centerText: { textAlign: "center" },
    removeBlueOutline: {
      ...Platform.select({
        web: {
          outlineStyle: "none",
        } as any,
      }),
    },
  });

  let title = data?.Notes;
  if (!title) {
    title = [PaymentTypes.DOMUSO_CASH, PaymentTypes.RENT].includes(data?.type)
      ? translate("homeStack.payment")
      : startCase(data?.type.toLowerCase());
  }
  const date = moment.utc(data?.date).format("ll");
  const hasPaymentSourceAndCard = data?.payment_source_type && data?.last_four_digits;
  const hasCheckId = Boolean(data?.scan_check_id);
  const isCertifiedCheck = data?.check_type === CheckType.CERTIFIED;
  const paymentMethod = (() => {
    try {
      if (data?.description) {
        return;
      } else if (data?.type === PaymentTypes.DOMUSO_CASH) {
        return translate("homeStack.domusoOnlineCertified");
      } else if (hasCheckId && isCertifiedCheck) {
        return translate("homeStack.certifiedCheck");
      } else if (hasCheckId) {
        return translate("homeStack.personalCheck");
      } else if (data?.payment_source_type === PaymentSourceTypes.BANK_ACCOUNT) {
        return `Bank Account ***${data?.last_four_digits}`;
      } else if (hasPaymentSourceAndCard) {
        return `${data?.payment_source_card_type} ***${data?.last_four_digits}`;
      } else if (upperCase(data?.type) === PaymentTypes.CHARGE) {
        return translate("homeStack.charge");
      } else {
        return translate("homeStack.payment");
      }
    } catch(error) {
      return translate("homeStack.payment");
    }
  })();
  const methodOrDescTx = data?.description ? "homeStack.description" : data?.Notes ? "homeStack.category" : "homeStack.method";
  const methodOrDescVal = data?.description ? data?.description : paymentMethod;
  const balanceOrStatusTx = data?.status ? "homeStack.status" : "homeStack.balance";
  const balanceOrStatusVal = data?.status
    ? startCase(data?.status?.toLowerCase())
    : toWebUIDollars(data?.Balance);

  return (
    <BottomSheetModal ref={sheetRef}>
      <>
        <View style={styles.header}>
          <Text tx="homeStack.detail" style={styles.centerText} />
          <View style={styles.headerRow}>
            <View style={styles.navButtonContainer}>
              {showLeftNavButton && (
                <Pressable onPress={leftClickHandler}>
                  <View style={[styles.buttonContainer, styles.removeBlueOutline]}>
                    <Image
                      style={styles.navButton}
                      source={require("../../../assets/chevron-left.png")}
                    />
                  </View>
                </Pressable>
              )}
            </View>
            <Text style={styles.title}>{title}</Text>
            <View>
              <View style={styles.navButtonContainer}>
                {showRightNavButton && (
                  <Pressable onPress={rightClickHandler}>
                    <View style={[styles.buttonContainer, styles.removeBlueOutline]}>
                      <Image
                        style={styles.navButton}
                        source={require("../../../assets/chevron-right.png")}
                      />
                    </View>
                  </Pressable>
                )}
              </View>
            </View>
          </View>
        </View>
        <View style={styles.container}>
          <View style={styles.row}>
            <DataGroup styles={styles} tx="homeStack.date" val={date} />
            <DataGroup styles={styles} tx="homeStack.id" val={data?.orderId} />
          </View>
          <View style={styles.row}>
            <DataGroup styles={styles} tx="homeStack.activity" val={toWebUIDollars(data?.amount)} />
            <DataGroup styles={styles} tx={balanceOrStatusTx} val={balanceOrStatusVal} />
          </View>
          <View style={styles.row}>
            <DataGroup styles={styles} tx={methodOrDescTx} val={methodOrDescVal} />
          </View>
        </View>
      </>
    </BottomSheetModal>
  );
};

interface DataGroupProps {
  styles: any;
  tx: string;
  val: string;
}

const DataGroup = ({ styles, tx, val }: DataGroupProps) => {
  return (
    <View style={styles.column}>
      <View style={styles.lineContainer}>
        <View style={styles.line} />
      </View>
      <View style={styles.textContainer}>
        <Text style={styles.smallText} tx={tx} />
        <Text style={styles.bold}>{val}</Text>
      </View>
    </View>
  );
};
